import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCountry } from "@/lib/country";
import { APPROVED_TREATMENTS, NON_PHARMA, HOW_TO_CONNECT } from "@/lib/treatmentsData";
import { Pill, Sparkles, FlaskConical, ExternalLink, MapPin, ArrowRight, Globe } from "lucide-react";

/**
 * Treatments & Trials — country-aware page.
 *
 * Top: FDA / EMA / regional regulator-approved medications, tagged with
 *      availability per country so a French family doesn't think Rexulti
 *      is a thing in Paris.
 * Mid: Non-medication interventions (exercise, hearing aids, diet, CST).
 * Bottom: Live recruiting clinical trials from ClinicalTrials.gov filtered
 *      to the caregiver's country, plus a curated list of national portals
 *      that explain how to enrol.
 */
export default function Treatments() {
    const { country, t, meta } = useCountry();
    const [trials, setTrials] = useState(null);
    const [loadingTrials, setLoadingTrials] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoadingTrials(true);
        api.get("/trials/search", { params: { country, limit: 8 } })
            .then((r) => { if (!cancelled) setTrials(r.data); })
            .catch(() => { if (!cancelled) setTrials({ studies: [], error: true }); })
            .finally(() => { if (!cancelled) setLoadingTrials(false); });
        return () => { cancelled = true; };
    }, [country]);

    // Medications available where this user lives go first; everything else
    // becomes a "not yet available in {country}" muted block at the bottom.
    const available = APPROVED_TREATMENTS.filter((m) => m.availability.includes(country));
    const unavailable = APPROVED_TREATMENTS.filter((m) => !m.availability.includes(country));
    const connectLinks = HOW_TO_CONNECT[country] || HOW_TO_CONNECT.US;

    return (
        <div className="space-y-12 animate-fade-in-up" data-testid="treatments-page">
            <PageHero
                kicker={t("treatments.eyebrow")}
                kickerIcon={FlaskConical}
                title={t("treatments.title")}
                subtitle={t("treatments.intro", { country: `${meta.flag} ${meta.label}` })}
            />

            {/* Approved medications */}
            <section data-testid="treatments-meds-section">
                <div className="flex items-center gap-2 mb-4">
                    <Pill size={18} strokeWidth={1.8} className="text-teal-dark" />
                    <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#12776F]">
                        {t("treatments.approvedMeds")}
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    {available.map((m) => (
                        <article key={m.id} className="ml-card p-5 md:p-6" data-testid={`treatment-${m.id}`}>
                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                <h3 className="font-heading font-semibold text-lg text-[#1F2624]">{m.name}</h3>
                                <a href={m.link} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1">
                                    Learn more <ExternalLink size={12} strokeWidth={1.8} />
                                </a>
                            </div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold text-teal mt-1">{m.kind}</p>
                            <p className="text-xs text-[#4C5551] mt-0.5">{m.approved}</p>
                            <p className="mt-3 text-sm text-[#1F2624] leading-relaxed">{m.summary}</p>
                            <p className="mt-3 text-sm text-[#4C5551] leading-relaxed">
                                <strong className="text-[#1F2624]">Who qualifies:</strong> {m.whoQualifies}
                            </p>
                            <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">
                                <strong className="text-[#1F2624]">Side effects:</strong> {m.sideEffects}
                            </p>
                        </article>
                    ))}
                </div>

                {unavailable.length > 0 && (
                    <details className="mt-6 ml-card p-4 bg-cream-light">
                        <summary className="cursor-pointer text-sm font-medium text-[#4C5551]">
                            Not yet approved in {meta.label} — {unavailable.length} other drug{unavailable.length === 1 ? "" : "s"}
                        </summary>
                        <ul className="mt-3 space-y-2 text-sm text-[#4C5551]">
                            {unavailable.map((m) => (
                                <li key={m.id}>
                                    <strong className="text-[#1F2624]">{m.name}</strong> — {m.kind}. Approved in: {m.availability.join(", ")}.
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </section>

            {/* Non-pharma */}
            <section data-testid="treatments-nonpharma-section">
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={18} strokeWidth={1.8} className="text-yellow-dark" />
                    <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#12776F]">
                        {t("treatments.nonPharma")}
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {NON_PHARMA.map((n) => (
                        <article key={n.id} className="ml-card p-5 bg-yellow-soft/40 !border-yellow-soft" data-testid={`nonpharma-${n.id}`}>
                            <h3 className="font-heading font-semibold text-[#1F2624]">{n.name}</h3>
                            <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">{n.summary}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Live trials */}
            <section data-testid="treatments-trials-section">
                <div className="flex items-center gap-2 mb-4">
                    <FlaskConical size={18} strokeWidth={1.8} className="text-teal-dark" />
                    <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#12776F]">
                        {t("treatments.trials")}
                    </h2>
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-teal-soft text-teal-dark ml-1">
                        Live · ClinicalTrials.gov
                    </span>
                </div>

                {loadingTrials && (
                    <p className="text-[#4C5551] text-sm" data-testid="trials-loading">
                        {t("treatments.trialsLoading")}
                    </p>
                )}

                {!loadingTrials && trials && trials.studies && trials.studies.length === 0 && (
                    <p className="text-[#4C5551] text-sm" data-testid="trials-empty">
                        {t("treatments.trialsEmpty", { country: meta.label })}
                    </p>
                )}

                {!loadingTrials && trials && trials.studies && trials.studies.length > 0 && (
                    <ul className="space-y-3" data-testid="trials-list">
                        {trials.studies.map((s) => (
                            <li key={s.nct_id} className="ml-card p-5" data-testid={`trial-${s.nct_id}`}>
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <h3 className="font-heading font-semibold text-[#1F2624]">{s.title}</h3>
                                    <a href={s.url} target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1 shrink-0">
                                        {t("treatments.viewOnSource")} <ExternalLink size={12} strokeWidth={1.8} />
                                    </a>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                    <span className="px-2 py-0.5 rounded-2xl bg-stage_early/30 text-stage_early-dark font-medium">
                                        {t("treatments.status")}: {s.status}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-2xl bg-cream-dark text-[#4C5551] font-medium">
                                        {t("treatments.phase")}: {s.phase || "N/A"}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-2xl bg-cream-dark text-[#4C5551] font-medium">
                                        NCT: {s.nct_id}
                                    </span>
                                </div>
                                {s.sites_in_country && s.sites_in_country.length > 0 && (
                                    <p className="mt-2 text-xs text-[#4C5551] flex items-start gap-1.5">
                                        <MapPin size={12} strokeWidth={1.8} className="shrink-0 mt-0.5" />
                                        <span>
                                            {s.sites_in_country.slice(0, 3).map((loc, i) => (
                                                <span key={i}>
                                                    {loc.facility ? `${loc.facility} — ` : ""}{loc.city}{i < s.sites_in_country.length - 1 ? "; " : ""}
                                                </span>
                                            ))}
                                        </span>
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* How to get connected */}
            <section className="ml-card p-6 md:p-8 bg-cream-light" data-testid="treatments-connect-section">
                <div className="flex items-center gap-2 mb-4">
                    <Globe size={18} strokeWidth={1.8} className="text-teal-dark" />
                    <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#12776F]">
                        {t("treatments.howToConnect")} — {meta.flag} {meta.label}
                    </h2>
                </div>
                <ul className="space-y-3">
                    {connectLinks.map((link) => (
                        <li key={link.url} className="flex items-start gap-3" data-testid={`connect-${link.url.slice(8, 30)}`}>
                            <ArrowRight size={16} strokeWidth={1.8} className="text-teal mt-1 shrink-0" />
                            <div>
                                <a href={link.url} target="_blank" rel="noopener noreferrer"
                                    className="font-medium text-teal-dark hover:underline">
                                    {link.name}
                                </a>
                                <p className="text-sm text-[#4C5551] mt-0.5">{link.desc}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <footer className="text-[11px] text-[#6E7A74] leading-snug">
                {t("treatments.disclaimer")}
            </footer>
        </div>
    );
}
